import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import StatCard from '../components/Dashboard/StatCard';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getAuthHeaders() {
  const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiFetch(url) {
  const res = await fetch(`${API_BASE}${url}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

/** Format YYYY-MM-DD */
function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const PRESETS = [
  { label: 'Today', from: () => fmtDate(new Date()), to: () => fmtDate(new Date()) },
  { label: 'Last 7 days', from: () => fmtDate(daysAgo(6)), to: () => fmtDate(new Date()) },
  { label: 'Last 30 days', from: () => fmtDate(daysAgo(29)), to: () => fmtDate(new Date()) },
  { label: 'Last 90 days', from: () => fmtDate(daysAgo(89)), to: () => fmtDate(new Date()) },
];

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe', '#43e97b', '#fa709a', '#fee140', '#a18cd1', '#fbc2eb'];

const QRAnalytics = () => {
  const [dateFrom, setDateFrom] = useState(fmtDate(daysAgo(29)));
  const [dateTo, setDateTo] = useState(fmtDate(new Date()));
  const [activePreset, setActivePreset] = useState('Last 30 days');

  const [topAttractions, setTopAttractions] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [hourData, setHourData] = useState([]);
  const [timeseriesData, setTimeseriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAttraction, setModalAttraction] = useState(null);
  const [modalDetail, setModalDetail] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `from=${dateFrom}&to=${dateTo}`;
      const [top, district, hour, ts] = await Promise.all([
        apiFetch(`/api/analytics/qr/top-attractions?${qs}&limit=10`),
        apiFetch(`/api/analytics/qr/by-district?${qs}`),
        apiFetch(`/api/analytics/qr/by-hour?${qs}`),
        apiFetch(`/api/analytics/qr/timeseries?${qs}&interval=day`),
      ]);
      setTopAttractions(top);
      setDistrictData(district);
      setHourData(hour);
      setTimeseriesData(ts);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Computed stats
  const totalScans = topAttractions.reduce((s, r) => s + r.scan_count, 0);
  const totalSessions = topAttractions.reduce((s, r) => s + r.unique_sessions, 0);
  const totalUsers = topAttractions.reduce((s, r) => s + r.unique_users, 0);

  // Preset click
  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setDateFrom(preset.from());
    setDateTo(preset.to());
  };

  // Custom date change
  const handleCustomFrom = (e) => { setActivePreset('Custom'); setDateFrom(e.target.value); };
  const handleCustomTo = (e) => { setActivePreset('Custom'); setDateTo(e.target.value); };

  // Drill-down
  const openModal = async (row) => {
    setModalAttraction(row);
    setModalOpen(true);
    setModalPage(1);
    await fetchDetail(row.attraction_id, 1);
  };

  const fetchDetail = async (id, page) => {
    setModalLoading(true);
    try {
      const data = await apiFetch(
        `/api/analytics/qr/attraction/${id}/detail?from=${dateFrom}&to=${dateTo}&page=${page}&limit=50`
      );
      setModalDetail(data);
    } catch (err) {
      console.error('Detail fetch error:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalPage = async (newPage) => {
    if (!modalAttraction) return;
    setModalPage(newPage);
    await fetchDetail(modalAttraction.attraction_id, newPage);
  };

  return (
    <Layout title="QR Analytics">
      <div style={{ padding: '20px' }}>
        {/* Date range filter */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p)}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                border: activePreset === p.label ? '2px solid #667eea' : '1px solid #ddd',
                background: activePreset === p.label ? '#667eea' : '#fff',
                color: activePreset === p.label ? '#fff' : '#333',
                cursor: 'pointer',
                fontWeight: activePreset === p.label ? '600' : '400',
              }}
            >
              {p.label}
            </button>
          ))}
          <span style={{ color: '#888', margin: '0 4px' }}>|</span>
          <input type="date" value={dateFrom} onChange={handleCustomFrom}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd' }} />
          <span style={{ color: '#888' }}>to</span>
          <input type="date" value={dateTo} onChange={handleCustomTo}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd' }} />
        </div>

        {error && (
          <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '16px', color: '#c33' }}>
            Error loading analytics: {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Loading analytics...</div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <StatCard title="Total Scans" value={totalScans.toLocaleString()} icon="QR" color="blue" />
              <StatCard title="Unique Sessions" value={totalSessions.toLocaleString()} icon="S" color="green" />
              <StatCard title="Unique Users" value={totalUsers.toLocaleString()} icon="U" color="purple" />
              <StatCard title="Attractions Scanned" value={topAttractions.length} icon="A" color="orange" />
            </div>

            {/* Top 10 table */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0 }}>Top 10 Attractions</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '10px 8px' }}>#</th>
                      <th style={{ padding: '10px 8px' }}>Name</th>
                      <th style={{ padding: '10px 8px' }}>District</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right' }}>Scans</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right' }}>Sessions</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right' }}>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topAttractions.map((row, i) => (
                      <tr
                        key={row.attraction_id}
                        onClick={() => openModal(row)}
                        style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7f8ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 8px', fontWeight: '600', color: '#888' }}>{i + 1}</td>
                        <td style={{ padding: '10px 8px' }}>{row.name || row.attraction_id}</td>
                        <td style={{ padding: '10px 8px', color: '#666' }}>{row.district || '-'}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>{row.scan_count}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right' }}>{row.unique_sessions}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right' }}>{row.unique_users}</td>
                      </tr>
                    ))}
                    {topAttractions.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>No scan data for this date range</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '24px' }}>
              {/* By District */}
              <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0 }}>Scans by District</h3>
                {districtData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, districtData.length * 40)}>
                    <BarChart data={districtData} layout="vertical" margin={{ left: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="district" width={90} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="scan_count" name="Scans">
                        {districtData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>No data</div>
                )}
              </div>

              {/* By Hour */}
              <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0 }}>Scans by Hour of Day</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={hourData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="scan_count" name="Scans" fill="#667eea" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timeseries */}
            <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0 }}>Scans Over Time</h3>
              {timeseriesData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeseriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="scan_count" stroke="#667eea" strokeWidth={2} name="Scans" dot={false} />
                    <Line type="monotone" dataKey="unique_sessions" stroke="#43e97b" strokeWidth={2} name="Sessions" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>No data</div>
              )}
            </div>
          </>
        )}

        {/* Drill-down modal */}
        {modalOpen && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => setModalOpen(false)}
          >
            <div
              style={{
                background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '800px',
                maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>{modalAttraction?.name || modalAttraction?.attraction_id}</h3>
                <button
                  onClick={() => setModalOpen(false)}
                  style={{
                    background: 'none', border: '1px solid #ddd', borderRadius: '6px',
                    padding: '4px 12px', cursor: 'pointer', fontSize: '16px',
                  }}
                >
                  Close
                </button>
              </div>

              {modalLoading && !modalDetail ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading...</div>
              ) : modalDetail ? (
                <>
                  {/* Totals */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ background: '#f7f8ff', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea' }}>{modalDetail.totals.scan_count}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Total Scans</div>
                    </div>
                    <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{modalDetail.totals.unique_sessions}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Unique Sessions</div>
                    </div>
                    <div style={{ background: '#fef9f0', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>{modalDetail.totals.gps_granted_count}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>GPS Granted</div>
                    </div>
                  </div>

                  {/* Source breakdown */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Source Breakdown</h4>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {Object.entries(modalDetail.totals.source_breakdown).map(([key, val]) => (
                        <div key={key} style={{ background: '#f8f8f8', borderRadius: '6px', padding: '8px 16px' }}>
                          <span style={{ fontWeight: '600' }}>{val}</span>
                          <span style={{ color: '#888', marginLeft: '6px', fontSize: '13px' }}>{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent scans table */}
                  <h4 style={{ marginBottom: '8px' }}>Recent Scans</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                          <th style={{ padding: '8px 6px' }}>Timestamp</th>
                          <th style={{ padding: '8px 6px' }}>User ID</th>
                          <th style={{ padding: '8px 6px' }}>City</th>
                          <th style={{ padding: '8px 6px' }}>Country</th>
                          <th style={{ padding: '8px 6px' }}>User Agent</th>
                          <th style={{ padding: '8px 6px' }}>GPS</th>
                          <th style={{ padding: '8px 6px' }}>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalDetail.recent_scans.map((scan, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {scan.scanned_at ? new Date(scan.scanned_at).toLocaleString() : '-'}
                            </td>
                            <td style={{ padding: '8px 6px' }}>{scan.user_id || '-'}</td>
                            <td style={{ padding: '8px 6px' }}>{scan.ip_city || '-'}</td>
                            <td style={{ padding: '8px 6px' }}>{scan.ip_country || '-'}</td>
                            <td style={{ padding: '8px 6px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={scan.user_agent}>
                              {scan.user_agent ? scan.user_agent.slice(0, 60) : '-'}
                            </td>
                            <td style={{ padding: '8px 6px' }}>{scan.gps_consent || '-'}</td>
                            <td style={{ padding: '8px 6px' }}>{scan.source || '-'}</td>
                          </tr>
                        ))}
                        {modalDetail.recent_scans.length === 0 && (
                          <tr><td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: '#aaa' }}>No scans</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {modalDetail.recent_scans.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                      <button
                        disabled={modalPage <= 1 || modalLoading}
                        onClick={() => handleModalPage(modalPage - 1)}
                        style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #ddd', cursor: modalPage <= 1 ? 'not-allowed' : 'pointer' }}
                      >
                        Prev
                      </button>
                      <span style={{ padding: '4px 8px', color: '#666' }}>Page {modalPage}</span>
                      <button
                        disabled={modalDetail.recent_scans.length < 50 || modalLoading}
                        onClick={() => handleModalPage(modalPage + 1)}
                        style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #ddd', cursor: modalDetail.recent_scans.length < 50 ? 'not-allowed' : 'pointer' }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QRAnalytics;
